import React, { useState } from "react";
import { FaCaretDown, FaCaretRight } from "react-icons/fa";
import ActionDropdown from "../components/ActionDropdown";

const flattenTreeData = (
  nodes,
  level = 0,
  parentCategoryId = null,
  counter = { current: 0 }
) => {
  let rows = [];

  if (!Array.isArray(nodes)) {
    return rows;
  }

  nodes.forEach((node) => {
    if (!node) return;

    const hasChildren = node.children && node.children.length > 0;

    // There is no need to check for active children as the count is already filtered in the materialService.getTreeData function
    // const activeChildrenCount = hasChildren
    //   ? node.children.filter((child) => child.status !== "D").length
    //   : 0;

    const type = level === 0 ? "category" : hasChildren ? "sub-branch" : "leaf";

    const uniqueKey = `${node.id}-${counter.current}`;
    counter.current += 1;

    rows.push({
      id: uniqueKey,
      category: node.category,
      description: node.description,
      unit: node.unit,
      point: node.point,
      status: node.status || "A",
      // There is no need to check for active children as the count is already filtered in the materialService.getTreeData function
      // count: activeChildrenCount, // Only count active children
      count: node.count,
      type,
      level,
      parentCategoryId,
      children: hasChildren ? node.children.map((child) => child.id) : [],
      originalId: node.id,
    });

    if (hasChildren) {
      rows = rows.concat(
        flattenTreeData(node.children, level + 1, uniqueKey, counter)
      );
    }
  });

  return rows;
};

export function CombinedDataTableTreeMenu({
  treeMenuData,
  tableColumns,
  tableActions,
  searchQuery = "",
}) {
  const [expandedCategories, setExpandedCategories] = useState([]);

  const filterData = (data, query) => {
    if (!query) return data;

    const lowerQuery = query.toLowerCase();
    const matchingIds = new Set();

    data.forEach((item) => {
      if (
        (item.category && item.category.toLowerCase().includes(lowerQuery)) ||
        (item.description &&
          item.description.toLowerCase().includes(lowerQuery)) ||
        (item.unit && item.unit.toLowerCase().includes(lowerQuery)) ||
        (item.point && item.point.toString().includes(lowerQuery)) ||
        (item.status && item.status.toLowerCase().includes(lowerQuery))
      ) {
        matchingIds.add(item.id);
      }
    });

    const includeParents = (id) => {
      const item = data.find((i) => i.id === id);
      if (!item || !item.parentCategoryId) return;

      matchingIds.add(item.parentCategoryId);
      includeParents(item.parentCategoryId);
    };

    Array.from(matchingIds).forEach((id) => includeParents(id));

    return data.filter((item) => matchingIds.has(item.id));
  };

  const tableData = React.useMemo(() => {
    const flattened = flattenTreeData(treeMenuData);
    return filterData(flattened, searchQuery);
  }, [treeMenuData, searchQuery]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const isRowVisible = (row) => {
    if (searchQuery) return true;

    let currentParentId = row.parentCategoryId;
    while (currentParentId) {
      if (!expandedCategories.includes(currentParentId)) {
        return false;
      }
      const parentRow = tableData.find((item) => item.id === currentParentId);
      currentParentId = parentRow?.parentCategoryId || null;
    }
    return true;
  };

  const getIndentStyle = (row) => {
    if (row.level === 0) return { paddingLeft: "0.5rem" };
    if (row.level === 1) return { paddingLeft: "2rem" };
    return { paddingLeft: `${2 + (row.level - 1) * 1.5}rem` };
  };

  return (
    <div className="flex flex-col space-y-8">
      <div className="w-full overflow-x-auto">
        <table className="min-w-full border-collapse text-sm sm:text-base">
          <thead>
            <tr className="border-b">
              {tableColumns.map((column) => (
                <th
                  key={column.header}
                  className="px-4 py-3 text-left font-semibold text-gray-700"
                >
                  {column.header}
                </th>
              ))}
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-32">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData
              .filter((row) => isRowVisible(row))
              .map((row) => {
                const indentStyle = getIndentStyle(row);
                const isDeactivated = row.status === "D";
                const isParent =
                  row.type === "category" || row.type === "sub-branch";

                return (
                  <tr
                    key={row.id}
                    className={`border-b hover:bg-gray-50 ${
                      isDeactivated ? "text-sm italic text-gray-500" : ""
                    }`}
                  >
                    <td className="py-3 font-medium">
                      <div className="flex items-center" style={indentStyle}>
                        {isParent && (
                          <button
                            onClick={() => toggleCategory(row.id)}
                            className="mr-2 text-gray-500 hover:text-gray-700"
                          >
                            {expandedCategories.includes(row.id) ? (
                              <FaCaretDown />
                            ) : (
                              <FaCaretRight />
                            )}
                          </button>
                        )}
                        {!isParent && <span className="w-6"></span>}
                        <span
                          className={
                            isDeactivated ? "text-gray-500" : "text-gray-800"
                          }
                        >
                          {row.category}
                          {isParent && row.count > 0 && (
                            <span
                              className={`ml-2 inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${
                                isDeactivated
                                  ? "bg-gray-100 text-gray-500"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {row.count}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3">{row.description || "-"}</td>

                    {!isParent && (
                      <td className="px-4 py-3 text-gray-600">
                        {row.unit || "-"}
                      </td>
                    )}
                    {isParent && <td className="px-4 py-3"></td>}

                    {!isParent && (
                      <td className="px-4 py-3 text-gray-600">
                        {row.point || "-"}
                      </td>
                    )}
                    {isParent && <td className="px-4 py-3"></td>}

                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end">
                        <ActionDropdown actions={tableActions(row)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
